import React from 'react';
import moment from 'moment';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import {
  CalendarMonthOutlined,
  Close,
  ContentCutOutlined,
  PaymentsOutlined,
  PersonOutline,
  ScheduleOutlined,
  StorefrontOutlined,
} from '@mui/icons-material';
import AppointmentWorkflowActions from './AppointmentWorkflowActions';

const DetailCard = ({ icon, label, value, secondaryValue }) => (
  <Box sx={{
    display: 'flex',
    gap: 1.5,
    p: 2,
    minHeight: 88,
    border: '1px solid #f0dfd0',
    borderRadius: 3,
    backgroundColor: '#fffaf4',
  }}>
    <Box sx={{ color: '#a45b2a', mt: 0.25 }}>{icon}</Box>
    <Box>
      <Typography
        variant="caption"
        sx={{ color: '#8a6a54', textTransform: 'uppercase', letterSpacing: 0.7 }}
      >
        {label}
      </Typography>
      <Typography sx={{ color: '#2f241e', fontWeight: 700 }}>{value || '—'}</Typography>
      {secondaryValue && (
        <Typography sx={{ color: '#8a6a54', mt: 0.5 }}>{secondaryValue}</Typography>
      )}
    </Box>
  </Box>
);

export default function AppointmentDetailsDialog({
  open,
  appointment,
  labels,
  busy = false,
  error,
  onClose,
  onConfirm,
  onModifyConfirm,
  onPropose,
  onCancel,
}) {
  if (!appointment) return null;
  const start = moment.parseZone(appointment.start_time);
  const end = moment.parseZone(appointment.end_time);
  const dateTime = start.isValid() && end.isValid()
    ? `${start.format('DD/MM/YYYY HH:mm')} – ${end.format('HH:mm')}`
    : '—';
  const duration = appointment.service_duration
    ? `${appointment.service_duration} min`
    : '—';
  const price = appointment.service_price !== undefined && appointment.service_price !== null
    ? `${appointment.service_price} DA`
    : '—';

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="appointment-details-title"
      PaperProps={{
        sx: {
          borderRadius: 4,
          overflow: 'hidden',
          backgroundColor: '#fffdf9',
          boxShadow: '0 24px 70px rgba(79, 51, 30, 0.22)',
        },
      }}
    >
      <DialogTitle sx={{ px: { xs: 2.5, sm: 4 }, pt: 3, pb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography
              component="span"
              id="appointment-details-title"
              variant="h5"
              sx={{ color: '#5e351f', fontWeight: 800, display: 'block' }}
            >
              {labels.title}
            </Typography>
            <Typography sx={{ color: '#8a6a54', mt: 0.5 }}>{appointment.client_name}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={labels.status}
              sx={{ backgroundColor: '#f7dfc8', color: '#7a3f1d', fontWeight: 700 }}
            />
            <IconButton aria-label={labels.close} disabled={busy} onClick={onClose}>
              <Close />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ px: { xs: 2.5, sm: 4 }, py: 3 }}>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 1.5,
        }}>
          <DetailCard
            icon={<PersonOutline />}
            label={labels.client}
            value={appointment.client_name}
            secondaryValue={appointment.client_phone}
          />
          <DetailCard icon={<ContentCutOutlined />} label={labels.service} value={appointment.service_name} />
          <DetailCard icon={<PersonOutline />} label={labels.employee} value={appointment.team_member_name} />
          <DetailCard icon={<StorefrontOutlined />} label={labels.salon} value={appointment.salon_name} />
          <DetailCard icon={<CalendarMonthOutlined />} label={labels.dateTime} value={dateTime} />
          <DetailCard icon={<ScheduleOutlined />} label={labels.duration} value={duration} />
          <DetailCard icon={<PaymentsOutlined />} label={labels.price} value={price} />
        </Box>
        {appointment.status === 'cancelled' && appointment.cancellation_reason && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <strong>{labels.cancellationReason} :</strong>{' '}
            {appointment.cancellation_reason}
          </Alert>
        )}
        {error && <Alert severity="warning" sx={{ mt: 2 }}>{error}</Alert>}
        <Box sx={{ mt: 2.5 }}>
          <AppointmentWorkflowActions
            appointment={appointment}
            busy={busy}
            onConfirm={onConfirm}
            onModifyConfirm={onModifyConfirm}
            onPropose={onPropose}
            onCancel={onCancel}
            labels={{
              confirm: labels.confirm,
              modify: labels.modify,
              propose: labels.propose,
              cancel: labels.cancel,
            }}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: { xs: 2.5, sm: 4 }, py: 2 }}>
        <Button disabled={busy} onClick={onClose} sx={{ color: '#7a3f1d', fontWeight: 700 }}>
          {labels.close}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
